// src/components/pages/admin-dash/UserModal.jsx
import React, { useEffect } from "react";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";

/**
 * Read-only user-details modal. Opened by clicking a row in UserTable
 * or from elsewhere in the admin app. Calls `onEdit(user)` to switch
 * over to the EditUserModal.
 */
export default function UserModal({ user, onClose, onEdit, onImpersonate }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!user) return null;

  const initials =
    (user.name || user.email || "?")
      .split(/\s+/)
      .map((s) => s[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join("") || "?";

  const credits = user.account?.credit ?? 0;
  const isActive = user.active === "TRUE";
  const isAdmin  = user.type === "ADMIN";

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl fade-in-up max-h-[90vh] overflow-y-auto"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-start gap-4 p-6"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div
            className="shrink-0 inline-flex items-center justify-center rounded-full font-semibold"
            style={{
              width: 56,
              height: 56,
              background: "var(--gold-dim)",
              color: "var(--gold)",
              border: "1px solid var(--gold-border)",
              fontSize: 18,
            }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h2
              className="font-semibold leading-tight"
              style={{ fontSize: 20, color: "var(--txt)" }}
            >
              {user.name || "—"}
            </h2>
            <div
              className="text-[13px] mt-0.5 truncate"
              style={{ color: "var(--txt-2)" }}
            >
              {user.email}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="neutral">User #{user.id}</Badge>
              {isAdmin ? (
                <Badge variant="gold">Admin</Badge>
              ) : (
                <Badge variant="neutral">User</Badge>
              )}
              {isActive ? (
                <Badge variant="teal">Active</Badge>
              ) : (
                <Badge variant="red">Inactive</Badge>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md transition-colors p-1.5"
            style={{ color: "var(--txt-2)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--surf-2)";
              e.currentTarget.style.color      = "var(--red)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color      = "var(--txt-2)";
            }}
            aria-label="Close"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Credits banner */}
          <div
            className="rounded-xl px-4 py-3 flex items-center justify-between gap-3"
            style={{
              background: credits > 0 ? "var(--gold-dim)" : "var(--surf-2)",
              border: `1px solid ${credits > 0 ? "var(--gold-border)" : "var(--border)"}`,
            }}
          >
            <div className="flex items-center gap-2">
              <Icon
                name="coin"
                size={18}
                style={{ color: credits > 0 ? "var(--gold)" : "var(--txt-3)" }}
              />
              <div>
                <div
                  className="font-medium text-[13px]"
                  style={{ color: "var(--txt)" }}
                >
                  Credit balance
                </div>
                <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
                  Spendable on pick generations
                </div>
              </div>
            </div>
            <div
              className="font-semibold tabular-nums"
              style={{
                fontSize: 22,
                color: credits > 0 ? "var(--gold)" : "var(--txt-2)",
              }}
            >
              {credits}
            </div>
          </div>

          {/* Details grid */}
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
            <DetailField label="Name"    value={user.name} />
            <DetailField label="Email"   value={user.email} />
            <DetailField label="User ID" value={user.id} mono />
            <DetailField label="Type"    value={user.type} />
            <DetailField label="Status"  value={isActive ? "Active" : "Inactive"} />
            <DetailField label="Code"    value={user.code} mono />
            {user.createdAt && (
              <DetailField
                label="Joined"
                value={new Date(user.createdAt).toLocaleDateString()}
              />
            )}
            {user.lastLoginAt && (
              <DetailField
                label="Last login"
                value={new Date(user.lastLoginAt).toLocaleString()}
              />
            )}
            {user.subscription?.plan && (
              <DetailField
                label="Subscription"
                value={user.subscription.plan}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex flex-wrap items-center justify-end gap-2 px-6 py-4"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          {onImpersonate && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => onImpersonate(user)}
              icon="user-shield"
              title="Log in as this user to reproduce what they see"
            >
              View as
            </Button>
          )}
          <Button size="sm" onClick={() => onEdit(user)} icon="pencil">
            Edit user
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div>
      <div className="lg-label mb-1">{label}</div>
      <div
        className={`text-[13.5px] ${mono ? "tabular-nums" : ""}`}
        style={{ color: "var(--txt)", wordBreak: "break-word" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}
